import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import { ArrowLeft, Car, CheckCircle2, MessageSquare, Shield } from "lucide-react";
import { useEffect, useState } from "react";

import { Gallery, type Photo } from "./galerie.js";
import type { Coverage, Estimate } from "../session/useSession.js";

/**
 * `round CW`, `CW.3`: **the fiche, `WF-CLI-04`.**
 *
 * `round C3-B`, the consolidated amendment: **rebuilt head to foot to the
 * 9 September drawing** (`hakili-client.jsx`, `Vehicule`). The founder's read
 * of the pairs found one root under three refuted rows: the fiche was the
 * 8 September drawing where the 9 September drawing became the record at
 * `M0-2`, and nobody rebuilt it. So: the dealer line with its icon, the
 * seller's city and quarter, the « Fiabilité » chip greyed with its round;
 * « Combien cela coûte par mois » with « Apport · 15 % · <amount> FCFA » and
 * « Durée · 60 mois »; the box with its label, the figure and ONE sentence;
 * « Comptant ou à crédit » as two cards; « Ce qui est vérifié avant que vous
 * achetiez » with the shield, the four rows with their green check and
 * sub-sentence, the refund sentence, the fee; « Dans quel ordre cela se
 * passe » as the stepper with the first disc lit; the two buttons and the
 * « Gratuit … » line.
 *
 * ## No money is computed here that the buyer will be held to
 *
 * Every figure is a door's: the estimate, the deposit in francs, the base
 * financed and the kept cash are `POST /simulation/quote`'s answer with
 * Hakili's fee inside (`C3-B.0`); the verification fee is
 * `GET /payments/verification-fees/quote`'s for THIS vehicle , the amount the
 * platform would ask, never `feeDue()` on the screen; the control date is
 * `GET /expertise/coverage`'s. A door that has not answered is a dash (`R81`).
 */

export interface VehicleDetail {
  readonly id: string;
  readonly title: string;
  readonly year: number | null;
  readonly mileageKm: number | null;
  readonly colour: string | null;
  readonly gearbox: string | null;
  readonly fuel: string | null;
  readonly seats: number | null;
  readonly city: string | null;
  readonly district: string | null;
  readonly priceAmount: number;
  readonly hasValidReport: boolean;
  /** `round W-13`, `M2`: a buyer's hold stands on the car, the fiche door's live word. */
  readonly engaged: boolean;
  /** `CW.3b`: asked through the port at read time, never copied. `null` is a dash. */
  readonly sellerName: string | null;
  /** The seller's grade, `null` until a door computes it (`WF-DLR-15`). */
  readonly reliability: string | null;
  readonly photos: readonly Photo[];
  /** `null` when no centroid answers , a dash, never a zero (`05-37`). */
  readonly distanceKm: number | null;
  readonly travelTime: string | null;
}

/** `GET /payments/verification-fees/quote`'s answer for this vehicle. */
export interface VerificationFeeQuote {
  readonly clientAmount: number | null;
  /** `PAY-P`: the door's sentence while no price is published. */
  readonly unpublished?: string;
  readonly reducedFlatApplied: boolean;
}

export interface VehicleDetailProps {
  readonly vehicule: VehicleDetail;
  /**
   * `round C3-B`, `C3-B.0`: **the one estimate, by the door.** The fiche's
   * simulator asks it on every slider move and composes nothing; what it
   * shows is `POST /simulation/quote`'s answer with Hakili's fee inside the
   * loan, the same answer the card, the eligibility result and the mandate
   * read. `null` when a door could not answer , a dash.
   */
  readonly estimate: (input: {
    readonly priceAmount: number;
    readonly depositPct: number;
    readonly durationMonths: number;
  }) => Promise<Estimate | null>;
  /** `C3-B.2`: the coverage banner and the control date, from expertise's days; `null` until read. */
  readonly coverage: Coverage | null;
  /** The amendment (1d): the fee for this vehicle, by payments; `null` until read , a dash. */
  readonly verificationFee: VerificationFeeQuote | null;
  /** The buyer's own city, for the distance card's sentence. */
  readonly buyerCity: string | null;
  readonly onBack: () => void;
  readonly onCheckEligibility: (simulation: {
    readonly vehicleId: string;
    readonly depositPct: number;
    readonly durationMonths: number;
    readonly amountFinanced: number;
    readonly monthlyAmount: number;
  }) => void;
  readonly onAskVisit: () => void;
}

/** Beyond this the drawing shows its distance card: it is a journey, not an errand. */
const LOIN_KM = 100;

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

/** The drawing's four verification rows, each a title and its sub-sentence. */
const CHECKS = [
  ["client.vehicule.check1", "client.vehicule.check1Detail"],
  ["client.vehicule.check2", "client.vehicule.check2Detail"],
  ["client.vehicule.check3", "client.vehicule.check3Detail"],
  ["client.vehicule.check4", "client.vehicule.check4Detail"],
] as const;

export function VehicleSheet({
  vehicule,
  estimate,
  coverage,
  verificationFee,
  buyerCity,
  onBack,
  onCheckEligibility,
  onAskVisit,
}: VehicleDetailProps): React.JSX.Element {
  const [depositPct, setApport] = useState(15);
  const [durationMonths, setDuree] = useState(60);
  /**
   * The door's answer for the sliders as they stand. Asked on every move; a
   * stale answer (the buyer moved again before it landed) is dropped by the
   * run guard, so the figure shown is always the one for the inputs shown.
   */
  const [reading, setReading] = useState<Estimate | null>(null);
  useEffect(() => {
    const run = { live: true };
    void estimate({ priceAmount: vehicule.priceAmount, depositPct, durationMonths })
      .then((answer) => {
        if (run.live) setReading(answer);
      })
      .catch(() => {
        if (run.live) setReading(null);
      });
    return () => {
      run.live = false;
    };
  }, [estimate, vehicule.priceAmount, depositPct, durationMonths]);
  // The distance, once it is known to be a journey; `far` keeps the card and its sentence together.
  const far = vehicule.distanceKm !== null && vehicule.distanceKm > LOIN_KM ? vehicule.distanceKm : null;
  const fee =
    verificationFee?.clientAmount === null || verificationFee === null
      ? DASH
      : formatMoney(verificationFee.clientAmount);
  const controlDay = coverage?.nextControlDay?.label ?? DASH;
  const disc = (lit: boolean): string =>
    `flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
      lit ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500"
    }`;

  return (
    <div className="-mx-4 flex flex-col pb-4">
      <Gallery
        photos={vehicule.photos}
        overImage={
          <button
            type="button"
            data-testid="vehicule-retour"
            aria-label={t("common.action.retour")}
            onClick={onBack}
            className="absolute left-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white"
          >
            <ArrowLeft className="h-4 w-4 text-gray-600" aria-hidden="true" />
          </button>
        }
      />

      {/* The head, as drawn: title and year, the facts line, the price, the
          dealer line with its icon, the seller's place, the grade chip. */}
      <div className="px-5 pt-4">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">
          {vehicule.title}
          {vehicule.year === null ? "" : ` ${String(vehicule.year)}`}
        </h1>
        <p className="mt-1 text-sm tabular-nums text-gray-500">
          {[
            vehicule.mileageKm === null
              ? null
              : `${formatQuantity(vehicule.mileageKm)} ${t("client.carte.km")}`,
            vehicule.gearbox,
            vehicule.fuel,
            vehicule.seats === null ? null : `${String(vehicule.seats)} ${t("client.vehicule.places")}`,
          ]
            .filter((part): part is string => part !== null && part !== "")
            .join(" · ")}
        </p>
        <p className="mt-3 text-2xl font-semibold tabular-nums text-gray-900">
          {formatMoney(vehicule.priceAmount)}
        </p>

        <div className="mt-3 flex items-center gap-2.5 border-b border-gray-100 pb-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
            <Car className="h-4 w-4 text-gray-500" aria-hidden="true" />
          </div>
          <div className="flex-1">
            {/* The seller: the NAME is `identity`'s through the port (`CW.3b`);
                the place is the car's as listed, prefilled from the address the
                dealership recorded by its own door , a dash while none is. */}
            <p className="text-sm font-medium text-gray-900" data-testid="vehicule-vendeur">
              {vehicule.sellerName ?? DASH}
            </p>
            <p className="text-xs text-gray-500" data-testid="vehicule-vendeur-lieu">
              {vehicule.city ?? DASH}, {vehicule.district ?? DASH}
              {vehicule.distanceKm !== null && vehicule.distanceKm > 0
                ? ` · ${fill("client.vehicule.aKmDeVous", { km: String(vehicule.distanceKm) })}`
                : ""}
            </p>
          </div>
          {/* The drawing's chip. Greyed with its round in its title until a
              door computes the grade , `R81`, never a flattering `A`. */}
          {vehicule.reliability === null ? (
            <span
              data-testid="vehicule-fiabilite"
              title={t("client.vehicule.fiabiliteAbsente")}
              aria-disabled="true"
              className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-bold text-gray-400"
            >
              {t("client.vehicule.fiabilite")} {DASH}
            </span>
          ) : (
            <span
              data-testid="vehicule-fiabilite"
              className="rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700"
            >
              {t("client.vehicule.fiabilite")} {vehicule.reliability}
            </span>
          )}
        </div>
      </div>

      {/* The distance card, beyond 100 km, as drawn. */}
      {far !== null ? (
        <div className="px-5 pt-4">
          <div
            data-testid="vehicule-distance"
            className="overflow-hidden rounded-2xl border border-amber-200 bg-white"
          >
            <div className="border-b border-amber-100 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">
                {fill("client.vehicule.loinTitre", { km: String(far) })}
              </p>
              <p className="mt-0.5 text-xs text-gray-600">
                {fill("client.vehicule.loinDepuis", {
                  trajet: vehicule.travelTime ?? DASH,
                  ville: buyerCity ?? DASH,
                })}
              </p>
            </div>
            <ul className="divide-y divide-gray-50">
              <li className="flex items-start gap-2.5 px-4 py-3">
                <Shield className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-700">{t("client.vehicule.loinExpertiseTitre")}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                    {t("client.vehicule.loinExpertiseDetail")}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-2.5 px-4 py-3">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-700">{t("client.vehicule.loinVideoTitre")}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                    {t("client.vehicule.loinVideoDetail")}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      ) : null}

      {/* `C3-B.2`: outside coverage, the buyer learns it at the top of the
          fiche, in the drawing's words , DERIVED from expertise's cabinets. */}
      {coverage !== null && !coverage.covered ? (
        <div className="px-5 pt-4">
          <div
            data-testid="vehicule-hors-couverture"
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5"
          >
            <p className="text-sm font-medium text-gray-900">
              {fill("client.vehicule.horsCouvertureTitre", { city: coverage.city ?? DASH })}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-gray-700">
              {t("client.vehicule.horsCouvertureTexte")}
            </p>
          </div>
        </div>
      ) : null}

      {/* The simulator, as drawn: the section's title, the two sliders with
          their values at the right, the box with label, figure, ONE sentence. */}
      {/* `W-13`, `M2`: the hold said on the fiche; the simulation and the
          eligibility check below stay free , the file's birth is what the
          hold refuses, and « Ouvrir mon dossier » says so where it is. */}
      {vehicule.engaged ? (
        <p
          className="mx-5 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700"
          data-testid="vehicule-engage"
        >
          {t("client.vehicule.engage")}
        </p>
      ) : null}
      <div className="px-5 pt-4" data-testid="vehicule-simulateur">
        <h2 className="mb-3 text-sm font-semibold text-gray-900">{t("client.vehicule.simulateur")}</h2>
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("client.vehicule.apport")}</span>
            <span
              className="text-sm font-medium tabular-nums text-gray-900"
              data-testid="vehicule-apport-valeur"
            >
              {depositPct} % · {reading === null ? DASH : formatMoney(reading.depositAmount)}
            </span>
          </div>
          <label className="sr-only" htmlFor="vehicule-apport">
            {t("client.vehicule.apport")}
          </label>
          <input
            id="vehicule-apport"
            data-testid="vehicule-apport"
            type="range"
            min="10"
            max="50"
            step="5"
            value={depositPct}
            onChange={(event) => {
              setApport(Number(event.target.value));
            }}
            className="mb-4 w-full"
          />
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("client.vehicule.duree")}</span>
            <span className="text-sm font-medium tabular-nums text-gray-900">
              {durationMonths} {t("client.vehicule.mois")}
            </span>
          </div>
          <label className="sr-only" htmlFor="vehicule-duree">
            {t("client.vehicule.duree")}
          </label>
          <input
            id="vehicule-duree"
            data-testid="vehicule-duree"
            type="range"
            min="24"
            max="60"
            step="12"
            value={durationMonths}
            onChange={(event) => {
              setDuree(Number(event.target.value));
            }}
            className="mb-4 w-full"
          />
          <div className="rounded-xl bg-indigo-50 px-4 py-3">
            <p className="text-xs text-gray-600">{t("client.vehicule.mensualiteEstimee")}</p>
            <p
              className="mt-0.5 text-2xl font-semibold tabular-nums text-indigo-700"
              data-testid="vehicule-mensualite"
            >
              {/* The door's estimate, or a dash: the screen composes no figure (`R81`). */}
              {reading === null ? DASH : formatMoney(reading.estimateAmount)}
            </p>
            <p className="mt-1 text-xs text-gray-500">{t("client.vehicule.estimationIndicative")}</p>
          </div>
        </div>
      </div>

      {/* « Comptant ou à crédit », two cards side by side, as drawn. */}
      <div className="px-5 pt-5" data-testid="vehicule-comparaison">
        <h2 className="mb-3 text-sm font-semibold text-gray-900">{t("client.vehicule.comparaison")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs text-gray-500">{t("client.vehicule.comptant")}</p>
            <p className="mt-1 text-base font-semibold tabular-nums text-gray-900">
              {formatQuantity(vehicule.priceAmount)}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-400">
              {t("client.vehicule.comptantSuite")}
            </p>
          </div>
          <div className="rounded-2xl border border-indigo-200 bg-white px-4 py-3">
            <p className="text-xs text-gray-500">{t("client.vehicule.credit")}</p>
            <p
              className="mt-1 text-base font-semibold tabular-nums text-indigo-700"
              data-testid="vehicule-credit"
            >
              {reading === null ? DASH : formatQuantity(reading.estimateAmount)}{" "}
              {t("client.vehicule.parMois")}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-400" data-testid="vehicule-credit-garde">
              {/* The drawing's « Vous gardez X »: the base financed, the door's own. */}
              {fill("client.vehicule.creditSuite", {
                amount: reading === null ? DASH : formatQuantity(reading.vehicleFinancedAmount),
              })}
            </p>
          </div>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-gray-400">{t("client.vehicule.comparaisonPied")}</p>
      </div>

      {/* The norm, as drawn: the shield, the four rows, the grey band with the
          refund sentence and the fee for THIS vehicle by payments' door. */}
      <div className="px-5 pt-5" data-testid="vehicule-verification">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
            <Shield className="h-4 w-4 text-indigo-600" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-gray-900">{t("client.vehicule.verification")}</h2>
          </div>
          <ul className="divide-y divide-gray-50">
            {CHECKS.map(([title, detail]) => (
              <li key={title} className="flex items-start gap-2.5 px-4 py-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                <div>
                  <p className="text-sm text-gray-700">{t(title)}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{t(detail)}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="bg-gray-50 px-4 py-3">
            <p className="text-xs leading-relaxed text-gray-500">{t("client.vehicule.jamaisSans")}</p>
            <div className="mt-2.5 flex items-start justify-between gap-3 border-t border-gray-200 pt-2.5">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-700">
                  {verificationFee?.reducedFlatApplied === true
                    ? t("client.vehicule.rapportDisponible")
                    : t("client.vehicule.frais")}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                  {verificationFee?.reducedFlatApplied === true
                    ? t("client.vehicule.rapportDisponibleDetail")
                    : t("client.vehicule.fraisRembourses")}
                </p>
              </div>
              <p
                className="shrink-0 text-sm font-semibold tabular-nums text-gray-900"
                data-testid="vehicule-frais"
              >
                {fee}
              </p>
            </div>
            {/* `PAY-P`: no price published , the door's own sentence under the dash (`R81`, `A47`). */}
            {verificationFee?.unpublished === undefined ? null : (
              <p className="mt-1 text-xs text-gray-400" data-testid="vehicule-frais-non-publie">
                {verificationFee.unpublished}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* The order of operations, as drawn: the stepper, the first disc lit. */}
      <div className="px-5 pt-5" data-testid="vehicule-ordre">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">{t("client.vehicule.ordre")}</h2>
          </div>
          <ul className="px-4 py-3">
            {(
              [
                ["client.vehicule.ordre1", fill("client.vehicule.ordre1Detail", { fee })],
                ["client.vehicule.ordre2", fill("client.vehicule.ordre2Date", { date: controlDay })],
                ["client.vehicule.ordre3", t("client.vehicule.ordre3Detail")],
                ["client.vehicule.ordre4", t("client.vehicule.ordre4Detail")],
              ] as const
            ).map(([title, detail], index) => (
              <li
                key={title}
                className="flex items-start gap-3 py-1.5"
                data-testid={`vehicule-etape-${String(index + 1)}`}
              >
                <span className={disc(index === 0)}>{index + 1}</span>
                <div>
                  <p className="text-sm leading-snug text-gray-700">{t(title)}</p>
                  <p
                    className="mt-0.5 text-xs text-gray-500"
                    data-testid={index === 1 ? "vehicule-controle-date" : undefined}
                  >
                    {detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <p className="bg-gray-50 px-4 py-3 text-xs leading-relaxed text-gray-500">
            {t("client.vehicule.pied")}
          </p>
        </div>
      </div>

      {/* The actions, as drawn. */}
      <div className="space-y-2.5 px-5 pt-5">
        <button
          type="button"
          data-testid="vehicule-eligibilite"
          disabled={reading === null}
          onClick={() => {
            if (reading === null) return;
            // **The simulation is STORED before it navigates.** `WF-CLI-04`'s
            // own acceptance criterion: reaching Éligibilité without one is
            // impossible, so the screen hands it over rather than letting the
            // next screen guess. The base financed is the door's own.
            onCheckEligibility({
              vehicleId: vehicule.id,
              depositPct,
              durationMonths,
              amountFinanced: reading.vehicleFinancedAmount,
              monthlyAmount: reading.estimateAmount,
            });
          }}
          className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
        >
          {t("client.vehicule.verifier")}
        </button>
        <button
          type="button"
          data-testid="vehicule-voir"
          onClick={onAskVisit}
          className="w-full rounded-2xl border border-gray-200 bg-white py-3.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {t("client.vehicule.voir")}
        </button>
        <p className="text-center text-xs leading-relaxed text-gray-400">{t("client.vehicule.gratuit")}</p>
      </div>
    </div>
  );
}
